import { OverlayScrollbars } from 'overlayscrollbars';

const INSTANCE_KEY = '__osInstance';

function shouldEnable() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  // Use OverlayScrollbars on all platforms (including iOS) to keep scrollbar visible.
  return true;
}

function hasOverlayScrollbars(el) {
  if (!el) return false;
  if (el[INSTANCE_KEY]) return true;
  if (el.hasAttribute && el.hasAttribute('data-overlayscrollbars')) return true;
  if (el.matches && el.matches('.os-host')) return true;
  return false;
}

function isOverlayInternal(el) {
  if (!el || !el.hasAttribute) return false;
  if (el.hasAttribute('data-overlayscrollbars-viewport')) return true;
  if (el.hasAttribute('data-overlayscrollbars-padding')) return true;
  if (el.hasAttribute('data-overlayscrollbars-content')) return true;
  if (el.hasAttribute('data-overlayscrollbars-scrollbar')) return true;
  if (el.classList && el.classList.contains('os-scrollbar')) return true;
  return false;
}

function isScrollable(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el === document.body || el === document.documentElement) return false;
  if (isOverlayInternal(el)) return false;

  const style = getComputedStyle(el);
  const overflowY = style.overflowY;
  const overflowX = style.overflowX;

  const canScrollY = overflowY === 'auto' || overflowY === 'scroll';
  const canScrollX = overflowX === 'auto' || overflowX === 'scroll';

  return canScrollY || canScrollX;
}

function createScrollbar(el) {
  if (hasOverlayScrollbars(el)) return;

  const instance = OverlayScrollbars(el, {
    scrollbars: {
      autoHide: 'never',
      autoHideDelay: 0,
      dragScroll: true,
      clickScroll: true,
    },
  });

  el.classList.add('os-mac-scrollbar');
  el[INSTANCE_KEY] = instance;
}

function refreshScrollbar(el) {
  if (!el || !el[INSTANCE_KEY]) return;
  el[INSTANCE_KEY].update();
}

function destroyScrollbar(el) {
  if (!el || !el[INSTANCE_KEY]) return;
  el[INSTANCE_KEY].destroy();
  delete el[INSTANCE_KEY];
  el.classList.remove('os-mac-scrollbar');
}

function applyForElement(el) {
  if (!el || el.nodeType !== 1) return;
  if (!isScrollable(el)) {
    destroyScrollbar(el);
    return;
  }
  createScrollbar(el);
}

function applyForSubtree(root) {
  if (!root || root.nodeType !== 1) return;

  applyForElement(root);

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
  let current = walker.nextNode();
  while (current) {
    applyForElement(current);
    current = walker.nextNode();
  }
}

function destroyForSubtree(root) {
  if (!root || root.nodeType !== 1) return;

  destroyScrollbar(root);

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
  let current = walker.nextNode();
  while (current) {
    destroyScrollbar(current);
    current = walker.nextNode();
  }
}

function getSelectors(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return [value];
  if (value && value.selectors) return value.selectors;
  return null;
}

function getSafeSelectors(selectors) {
  if (!Array.isArray(selectors)) return [];
  return selectors.filter((selector) => typeof selector === 'string' && selector.trim().length > 0);
}

function initForNode(node, selectors) {
  if (!node || node.nodeType !== 1 || !selectors.length) return;

  selectors.forEach((selector) => {
    try {
      if (node.matches && node.matches(selector)) {
        applyForElement(node);
      }
      if (node.querySelectorAll) {
        node.querySelectorAll(selector).forEach((target) => {
          applyForElement(target);
        });
      }
    } catch (e) {
      // invalid selector
    }
  });
}

function refreshForNode(node, selectors) {
  if (!node || node.nodeType !== 1 || !selectors.length) return;

  selectors.forEach((selector) => {
    try {
      if (node.matches && node.matches(selector)) {
        refreshScrollbar(node);
      }
      if (node.querySelectorAll) {
        node.querySelectorAll(selector).forEach((target) => {
          refreshScrollbar(target);
        });
      }
    } catch (e) {
      // invalid selector
    }
  });
}

function destroyForNode(node, selectors) {
  if (!node || node.nodeType !== 1 || !selectors.length) return;

  selectors.forEach((selector) => {
    try {
      if (node.matches && node.matches(selector)) {
        destroyScrollbar(node);
      }
      if (node.querySelectorAll) {
        node.querySelectorAll(selector).forEach((target) => {
          destroyScrollbar(target);
        });
      }
    } catch (e) {
      // invalid selector
    }
  });
}

export default {
  mounted(el, binding) {
    if (!shouldEnable()) return;

    const selectors = getSafeSelectors(getSelectors(binding.value));

    if (selectors.length) {
      initForNode(el, selectors);
      refreshForNode(el, selectors);

      const observer = new MutationObserver((mutations) => {
        let shouldRefresh = false;
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => initForNode(node, selectors));
            mutation.removedNodes.forEach((node) => destroyForNode(node, selectors));
            shouldRefresh = true;
          }
        });
        if (shouldRefresh) {
          refreshForNode(el, selectors);
        }
      });

      observer.observe(el, {
        childList: true,
        subtree: true,
      });

      const resizeHandler = () => {
        initForNode(el, selectors);
        refreshForNode(el, selectors);
      };
      window.addEventListener('resize', resizeHandler, { passive: true });

      el._osScrollbarObserver = observer;
      el._osScrollbarResizeHandler = resizeHandler;
      el._osScrollbarSelectors = selectors;
      return;
    }

    applyForSubtree(el);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => applyForSubtree(node));
          mutation.removedNodes.forEach((node) => destroyForSubtree(node));
        }
      });
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
    });

    const resizeHandler = () => applyForSubtree(el);
    window.addEventListener('resize', resizeHandler, { passive: true });

    el._osScrollbarObserver = observer;
    el._osScrollbarResizeHandler = resizeHandler;
  },

  unmounted(el) {
    if (el._osScrollbarObserver) {
      el._osScrollbarObserver.disconnect();
      delete el._osScrollbarObserver;
    }
    if (el._osScrollbarResizeHandler) {
      window.removeEventListener('resize', el._osScrollbarResizeHandler);
      delete el._osScrollbarResizeHandler;
    }

    if (el._osScrollbarSelectors) {
      destroyForNode(el, el._osScrollbarSelectors);
      delete el._osScrollbarSelectors;
      return;
    }

    destroyForSubtree(el);
  },
};
